import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "./store";

type Counter = "red" | "yellow" | null;

export interface RoomPlayer {
  socketId: string;
  name: string;
  color: "red" | "yellow";
  role: "p1" | "p2";
  fid?: number;
  username?: string;
  pfpUrl?: string;
}

export interface RoomSpectator {
  socketId: string;
  name: string;
  fid?: number;
  username?: string;
  pfpUrl?: string;
}

export interface RoomState {
  id: string;
  players: RoomPlayer[];
  spectators: RoomSpectator[];
  board: Counter[][];
  turn: "red" | "yellow";
  winner: string | null;
  isStarted: boolean;
  isPaused: boolean;
  pausedBy: "p1" | "p2" | null;
  pausedAt: string | null;
  scores: { p1: number; p2: number };
  timer: number;
  /** Votes to resume the match (p1/p2) */
  resumeVotes?: { p1?: boolean; p2?: boolean };
  /** True if the winner came from a forfeit rather than a played game. */
  forfeited?: boolean;
}

export interface RematchVotes {
  p1?: boolean;
  p2?: boolean;
}

interface MultiplayerState {
  isConnected: boolean;
  roomId: string | null;
  role: "p1" | "p2" | "spectator" | null;
  myColor: "red" | "yellow" | null;
  room: RoomState | null;
  error: string | null;
  isConnecting: boolean;
  /** Rematch vote tally since the last game ended. */
  rematchVotes: RematchVotes;
}

const initialState: MultiplayerState = {
  isConnected: false,
  roomId: null,
  role: null,
  myColor: null,
  room: null,
  error: null,
  isConnecting: false,
  rematchVotes: {},
};

const multiplayerSlice = createSlice({
  name: "multiplayer",
  initialState,
  reducers: {
    setConnected(state, action: PayloadAction<boolean>) {
      state.isConnected = action.payload;
      if (!action.payload) {
        state.isConnecting = false;
      }
    },
    setConnecting(state, action: PayloadAction<boolean>) {
      state.isConnecting = action.payload;
    },
    setRoomCreated(
      state,
      action: PayloadAction<{
        roomId: string;
        role: "p1";
        color: "red";
        roomState: RoomState;
      }>,
    ) {
      state.roomId = action.payload.roomId;
      state.role = action.payload.role;
      state.myColor = action.payload.color;
      state.room = action.payload.roomState;
      state.error = null;
      state.isConnecting = false;
    },
    setRoomJoined(
      state,
      action: PayloadAction<{
        roomId: string;
        role: "p2" | "spectator";
        color: "yellow" | null;
        roomState: RoomState;
      }>,
    ) {
      state.roomId = action.payload.roomId;
      state.role = action.payload.role;
      state.myColor = action.payload.color;
      state.room = action.payload.roomState;
      state.error = null;
      state.isConnecting = false;
    },
    setRoomRejoined(
      state,
      action: PayloadAction<{
        roomId: string;
        role: "p1" | "p2" | "spectator";
        color: "red" | "yellow" | null;
        roomState: RoomState;
      }>,
    ) {
      state.roomId = action.payload.roomId;
      state.role = action.payload.role;
      state.myColor = action.payload.color;
      state.room = action.payload.roomState;
      state.error = null;
      state.isConnecting = false;
    },
    setRoomUpdated(state, action: PayloadAction<RoomState>) {
      state.room = action.payload;
    },
    applyMove(
      state,
      action: PayloadAction<{
        col: number;
        row: number;
        color: "red" | "yellow";
        nextTurn: "red" | "yellow";
        winner: string | null;
        scores: { p1: number; p2: number };
        board: Counter[][];
      }>,
    ) {
      if (!state.room) return;
      state.room.board = action.payload.board;
      state.room.turn = action.payload.nextTurn;
      state.room.winner = action.payload.winner;
      state.room.scores = action.payload.scores;
      // Fresh game result — clear any stale rematch votes.
      if (action.payload.winner) {
        state.rematchVotes = {};
      }
    },
    applyGameRestarted(state, action: PayloadAction<RoomState>) {
      state.room = action.payload;
      state.rematchVotes = {};
    },
    applyRematchVote(
      state,
      action: PayloadAction<{ votes: RematchVotes; by: "p1" | "p2" }>,
    ) {
      state.rematchVotes = action.payload.votes;
    },
    applyResumeVote(
      state,
      action: PayloadAction<{ votes: { p1?: boolean; p2?: boolean }; by: "p2" | "p1" }>,
    ) {
      if (!state.room) return;
      state.room.resumeVotes = action.payload.votes;
    },
    applyMatchForfeited(
      state,
      action: PayloadAction<{
        forfeitedBy: "p1" | "p2";
        winner: string | null;
        roomState: RoomState;
      }>,
    ) {
      state.room = action.payload.roomState;
      if (state.room) {
        state.room.forfeited = true;
      }
    },
    applyTimerUpdate(state, action: PayloadAction<number>) {
      if (!state.room) return;
      state.room.timer = action.payload;
    },
    applyTurnTimeout(
      state,
      action: PayloadAction<{ nextTurn: "red" | "yellow" }>,
    ) {
      if (!state.room) return;
      state.room.turn = action.payload.nextTurn;
      state.room.timer = 15;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.isConnecting = false;
    },
    resetMultiplayer: () => initialState,
  },
});

export const {
  setConnected,
  setConnecting,
  setRoomCreated,
  setRoomJoined,
  setRoomRejoined,
  setRoomUpdated,
  applyMove,
  applyGameRestarted,
  applyRematchVote,
  applyResumeVote,
  applyMatchForfeited,
  applyTimerUpdate,
  applyTurnTimeout,
  setError,
  resetMultiplayer,
} = multiplayerSlice.actions;

export const selectMultiplayer = (state: RootState) => state.multiplayer;
export const selectMpRoom = (state: RootState) => state.multiplayer.room;
export const selectMpRole = (state: RootState) => state.multiplayer.role;
export const selectMpMyColor = (state: RootState) => state.multiplayer.myColor;
export const selectMpIsConnected = (state: RootState) =>
  state.multiplayer.isConnected;
export const selectMpError = (state: RootState) => state.multiplayer.error;
export const selectMpIsConnecting = (state: RootState) =>
  state.multiplayer.isConnecting;
export const selectMpRematchVotes = (state: RootState) =>
  state.multiplayer.rematchVotes;
export const selectMpResumeVotes = (state: RootState) => 
  state.multiplayer.room?.resumeVotes || {};

export const multiplayerReducer = multiplayerSlice.reducer;
